import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ChevronLeft, Send, Users, Eye, AlertTriangle,
  DoorOpen, Keyboard, X, Volume2
} from 'lucide-react';
import api from '../utils/api';
import { useI18n } from '../contexts/I18nContext';
import { toast } from 'sonner';

// Virtual Keyboard layouts
const KEYBOARD_LAYOUTS = {
  qwerty: [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm']
  ],
  hebrew: [
    ['ק', 'ר', 'א', 'ט', 'ו', 'ן', 'ם', 'פ'],
    ['ש', 'ד', 'ג', 'כ', 'ע', 'י', 'ח', 'ל', 'ך', 'ף'],
    ['ז', 'ס', 'ב', 'ה', 'נ', 'מ', 'צ', 'ת', 'ץ']
  ],
  greek: [
    ['ς', 'ε', 'ρ', 'τ', 'υ', 'θ', 'ι', 'ο', 'π'],
    ['α', 'σ', 'δ', 'φ', 'γ', 'η', 'ξ', 'κ', 'λ'],
    ['ζ', 'χ', 'ψ', 'ω', 'β', 'ν', 'μ']
  ],
  arabic: [
    ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
    ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك'],
    ['ئ', 'ء', 'ؤ', 'ر', 'ى', 'ة', 'و', 'ز', 'ظ']
  ],
  russian: [
    ['й', 'ц', 'у', 'к', 'е', 'н', 'г', 'ш', 'щ', 'з', 'х'],
    ['ф', 'ы', 'в', 'а', 'п', 'р', 'о', 'л', 'д', 'ж', 'э'],
    ['я', 'ч', 'с', 'м', 'и', 'т', 'ь', 'б', 'ю']
  ],
  chinese_pinyin: [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm']
  ],
  japanese_hiragana: [
    ['あ', 'い', 'う', 'え', 'お', 'か', 'き', 'く', 'け', 'こ'],
    ['さ', 'し', 'す', 'せ', 'そ', 'た', 'ち', 'つ', 'て', 'と'],
    ['な', 'に', 'ぬ', 'ね', 'の', 'は', 'ひ', 'ふ', 'へ', 'ほ'],
    ['ま', 'み', 'む', 'め', 'も', 'や', 'ゆ', 'よ'],
    ['ら', 'り', 'る', 'れ', 'ろ', 'わ', 'を', 'ん']
  ],
  korean: [
    ['ㅂ', 'ㅈ', 'ㄷ', 'ㄱ', 'ㅅ', 'ㅛ', 'ㅕ', 'ㅑ', 'ㅐ', 'ㅔ'],
    ['ㅁ', 'ㄴ', 'ㅇ', 'ㄹ', 'ㅎ', 'ㅗ', 'ㅓ', 'ㅏ', 'ㅣ'],
    ['ㅋ', 'ㅌ', 'ㅊ', 'ㅍ', 'ㅠ', 'ㅜ', 'ㅡ']
  ]
};

const LANGUAGE_KEYBOARD_MAP = {
  'general': 'qwerty',
  'hbo': 'hebrew',
  'grc': 'greek',
  'ara': 'arabic',
  'rus': 'russian',
  'cmn': 'chinese_pinyin',
  'jpn': 'japanese_hiragana',
  'kor': 'korean'
};

const ChatRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'watch';
  const { t } = useI18n();
  
  const [roomInfo, setRoomInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connected, setConnected] = useState(false);
  const [participants, setParticipants] = useState(0);
  const [watchers, setWatchers] = useState(0);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardLayout, setKeyboardLayout] = useState('qwerty');
  const [loading, setLoading] = useState(true);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    fetchRoomInfo();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [roomId, mode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchRoomInfo = async () => {
    // Close existing WebSocket before reconnecting
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    try {
      setLoading(true);
      setMessages([]); // Clear messages when reconnecting
      const response = await api.get(`/chat/room/${roomId}`);
      setRoomInfo(response.data);
      setParticipants(response.data.participants);
      setWatchers(response.data.watchers);
      
      // Set keyboard layout based on language
      const langCode = response.data.language_code || 'general';
      const layout = LANGUAGE_KEYBOARD_MAP[langCode] || 'qwerty';
      setKeyboardLayout(layout);
      
      // Connect to WebSocket
      connectWebSocket();
    } catch (err) {
      console.error('Failed to fetch room info:', err);
      toast.error('Failed to load chat room');
      navigate('/community');
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    // Prevent duplicate connections
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/api/chat/ws/${roomId}/${mode === 'participate' ? 'participate' : 'watch'}`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      setConnected(true);
      console.log('WebSocket connected');
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'welcome') {
        // Load recent messages from history
        if (data.recent_messages && Array.isArray(data.recent_messages)) {
          setMessages(data.recent_messages.map(msg => ({
            type: 'message',
            user_id: msg.user_id,
            username: msg.username,
            text: msg.text,
            timestamp: msg.timestamp
          })));
        }
      } else if (data.type === 'message') {
        // Prevent duplicate messages by checking timestamp
        setMessages(prev => {
          const isDuplicate = prev.some(msg => 
            msg.timestamp === data.timestamp && 
            msg.user_id === data.user_id && 
            msg.text === data.text
          );
          if (isDuplicate) return prev;
          return [...prev, data];
        });
      } else if (data.type === 'system') {
        setMessages(prev => [...prev, { ...data, isSystem: true }]);
      } else if (data.type === 'stats') {
        setParticipants(data.participants);
        setWatchers(data.watchers);
      }
    };
    
    ws.onclose = () => {
      setConnected(false);
      console.log('WebSocket disconnected');
      // Attempt to reconnect after a delay if the component is still mounted
      setTimeout(() => {
        if (wsRef.current === ws) {
          console.log('Attempting to reconnect WebSocket...');
          connectWebSocket();
        }
      }, 3000);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
    
    // Set up ping interval to keep connection alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Send ping every 30 seconds
    
    // Store the interval to clean it up later
    ws.pingInterval = pingInterval;
  };

  // Clean up ping interval when WebSocket closes
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        if (wsRef.current.pingInterval) {
          clearInterval(wsRef.current.pingInterval);
        }
        wsRef.current.close();
      }
    };
  }, []);

  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || mode !== 'participate') return;
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        text: newMessage.trim()
      }));
      setNewMessage('');
    }
  };

  const handleKeyboardInput = (char) => {
    setNewMessage(prev => prev + char);
    inputRef.current?.focus();
  };

  const handleBackspace = () => {
    setNewMessage(prev => prev.slice(0, -1));
  };

  const toggleKeyboardLayout = () => {
    if (keyboardLayout === 'qwerty') {
      const langCode = roomInfo?.language_code || 'general';
      const nativeLayout = LANGUAGE_KEYBOARD_MAP[langCode] || 'qwerty';
      setKeyboardLayout(nativeLayout);
    } else {
      setKeyboardLayout('qwerty');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/community')}
              className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-400 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white">
                {roomInfo?.language_name || t('chat.chatRoom')}
              </h1>
            <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <DoorOpen className="w-4 h-4" />
                  {participants} {t('chat.participating')}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {watchers} {t('chat.watching')}
                </span>
                <span className={`flex items-center gap-1 ${connected ? 'text-emerald-400' : 'text-red-400'}`}>
                  <span className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-red-400'}`}></span>
                  {connected ? t('chat.connected') : t('chat.disconnected')}
                </span>
              </div>
            </div>
          </div>
          
          {mode === 'participate' && (
            <button
              onClick={() => setShowKeyboard(!showKeyboard)}
              className={`p-2 rounded-lg transition-colors ${
                showKeyboard ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
              }`}
              data-testid="toggle-keyboard"
            >
              <Keyboard className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-500/10 border-b border-yellow-500/30 px-4 py-2">
        <p className="text-xs text-yellow-200 text-center flex items-center justify-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {t('chat.disclaimer')}
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-slate-500 py-10">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('chat.noMessages')}</p>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`${msg.isSystem ? 'text-center' : 'flex items-start gap-3'}`}
            >
              {msg.isSystem ? (
                <span className="text-sm text-slate-500 italic">{msg.text}</span>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-400 text-sm font-medium">
                    {msg.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium text-white">{msg.username}</span>
                      <span className="text-xs text-slate-500">{formatTime(msg.timestamp)}</span>
                    </div>
                    <p className="text-slate-300 mt-1">{msg.text}</p>
                  </div>
                </>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Virtual Keyboard (Desktop) */}
      {showKeyboard && !isMobile && mode === 'participate' && (
        <div className="bg-slate-800 border-t border-slate-700 p-4">
          <div className="max-w-2xl mx-auto">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-slate-400">
                {keyboardLayout === 'qwerty' ? t('chat.qwertyKeyboard') : t('chat.languageKeyboard', { language: roomInfo?.language_name })}
              </span>
              <button
                onClick={toggleKeyboardLayout}
                className="text-sm px-3 py-1 rounded bg-slate-700 text-slate-300 hover:bg-slate-600"
              >
                {t('chat.switchTo', { layout: keyboardLayout === 'qwerty' ? roomInfo?.language_name : t('chat.qwerty') })}
              </button>
            </div>
            <div className="space-y-2">
              {KEYBOARD_LAYOUTS[keyboardLayout]?.map((row, rowIdx) => (
                <div key={rowIdx} className="flex justify-center gap-1">
                  {row.map((key, keyIdx) => (
                    <button
                      key={keyIdx}
                      onClick={() => handleKeyboardInput(key)}
                      className="w-10 h-10 rounded bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors"
                    >
                      {key}
                    </button>
                  ))}
                </div>
              ))}
              <div className="flex justify-center gap-2 mt-2">
                <button
                  onClick={() => handleKeyboardInput(' ')}
                  className="w-40 h-10 rounded bg-slate-700 hover:bg-slate-600 text-slate-400 transition-colors"
                >
                  {t('chat.space')}
                </button>
                <button
                  onClick={handleBackspace}
                  className="w-20 h-10 rounded bg-slate-700 hover:bg-slate-600 text-slate-400 transition-colors"
                >
                  {t('chat.back')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      {mode === 'participate' ? (
        <form onSubmit={sendMessage} className="bg-slate-800 border-t border-slate-700 p-4">
          <div className="max-w-4xl mx-auto flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={t('chat.typeMessage')}
              className="flex-1 px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              maxLength={1000}
              disabled={!connected}
              dir={['hebrew', 'arabic'].includes(keyboardLayout) ? 'rtl' : 'ltr'}
            />
            <button
              type="submit"
              disabled={!connected || !newMessage.trim()}
              className="px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium transition-colors"
              data-testid="send-message"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-slate-800 border-t border-slate-700 p-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-400 mb-3">{t('chat.watchingRoom')}</p>
            <button
              onClick={() => navigate(`/community/chat/${roomId}?mode=participate`)}
              className="px-6 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium transition-colors"
            >
              <DoorOpen className="w-5 h-5 inline mr-2" />
              {t('chat.joinParticipant')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatRoom;
